import Icon from "@/components/Icon";
import { loginStore } from "@/modules/login/store";
import { accelerationStore } from "@/stores/acceleration";
import { WithClassName } from "@/types/base";
import { genBlock } from "@/utils/bem";
import { useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import {
  Box,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  chakra,
  Input,
} from "@chakra-ui/react";
import { langStore } from "@noli/business";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LabelText } from "../../styleComponents/Texts";
import { ReactComponent as MinusButtonSvg } from "@/assets/icons/minus_button.svg";
import { ReactComponent as PlusButtonSvg } from "@/assets/icons/plus_button.svg";

const block = genBlock("c-gen-multi-input");
const disabledWrapStyle = {
  cursor: "not-allowed",
};
const iconButtonStyle = (disabled: boolean) => ({
  color: disabled ? "text.primary" : "line.darkGray",
  fontSize: "18px",
  cursor: disabled ? "not-allowed" : "pointer",
});
const _GenMultiInput: React.FC<
  WithClassName<{
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
  }>
> = ({ className, value = 1, onChange, min = 1, max = 1 }) => {
  const { translate } = useTranslate();
  const lang = langStore.tgt;
  const { isAccelerated } = accelerationStore;
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState(value?.toString() ?? "1");

  const isMultiDisabled = useMemo(() => {
    // return false;
    return loginStore.isGuest || !isAccelerated;
  }, [isAccelerated, loginStore.isGuest]);

  useEffect(() => {
    if (isMultiDisabled && value > 1) {
      setInputVal("1");
      onChange(1);
    }
  }, [isMultiDisabled]);

  useEffect(() => {
    if (value && value.toString() !== inputVal) {
      setInputVal(value.toString());
    }
  }, [value]);

  const inputValueFormatter = useCallback(
    (input: number) => {
      const step = 1;
      let val = input;
      if (val <= min) {
        val = min;
      } else if (val >= max) {
        val = max;
      } else {
        if ((val % 1) / step < 0.5) {
          val = val - (val % step);
        } else {
          val = val + (step - (val % step));
        }
        val = +val.toFixed(0);
      }
      return val;
    },
    [min, max],
  );

  const handleInputChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setInputVal(e.target.value);
    },
    [setInputVal],
  );

  const handleInputBlur = useCallback(
    (inputVal: number) => {
      const val = inputValueFormatter(inputVal);
      console.log("handleInputBlur: ", val);
      setInputVal(val.toString());
      // setVal(val);
      onChange?.(val);
    },
    [onChange, setInputVal, inputValueFormatter],
  );

  const handleInputPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === "Enter") {
        const val = inputValueFormatter(+e.currentTarget.value);
        setInputVal(val.toString());
        onChange?.(val);
      }
    },
    [onChange, setInputVal, inputValueFormatter],
  );

  const isMinusDisabled = useMemo(() => {
    if (isMultiDisabled) return true;
    if (+inputVal > min) return false;
    return true;
  }, [isMultiDisabled, min, inputVal]);

  const isPlusDisabled = useMemo(() => {
    if (isMultiDisabled) return true;
    if (+inputVal < max) return false;
    return true;
  }, [isMultiDisabled, max, inputVal]);

  const handleMinus = useCallback(() => {
    if (isMinusDisabled) return;
    const val = inputValueFormatter(+inputVal - 1);
    setInputVal(val.toString());
    onChange?.(val);
  }, [isMinusDisabled, inputVal, setInputVal, onChange, inputValueFormatter]);

  const handlePlus = useCallback(() => {
    if (isPlusDisabled) return;
    const val = inputValueFormatter(+inputVal + 1);
    setInputVal(val.toString());
    onChange?.(val);
  }, [isPlusDisabled, inputVal, setInputVal, onChange, inputValueFormatter]);

  return (
    <Flex w="100%" alignItems="center" gap="8px" className={classNames(className, block())}>
      <LabelText className={block("title")} whiteSpace="nowrap" color="#FFFFFF" fontWeight={500}>
        {`${translate(Editor_Words["label_text_generate_count"])}：`}
      </LabelText>
      <Flex className={block({ e: "input_wrapper" })} flex={1}>
        <Popover
          trigger="hover"
          onOpen={() => {
            if (!isMultiDisabled) return;
            setOpen(true);
          }}
          // isLazy
          eventListeners={false}
          arrowSize={10}
          arrowShadowColor="none"
          placement="bottom-start"
          offset={[0, 18]}
          boundary={document.body}
          strategy="fixed"
          onClose={() => {
            setOpen(false);
          }}
          closeOnBlur={false}
          isOpen={open}
          modifiers={[
            {
              name: "topLogger",
              enabled: true,
              phase: "main",
              fn: ({ state }) => {
                state.styles.popper.zIndex = "1000";
                return state;
              },
            },
          ]}
        >
          <PopoverTrigger>
            <Flex
              tabIndex={0}
              alignItems="center"
              w="100%"
              {...(isMultiDisabled ? disabledWrapStyle : null)}
            >
              <Icon
                svg={MinusButtonSvg}
                strokeByCurrentColor
                {...iconButtonStyle(isMinusDisabled)}
                onClick={handleMinus}
              />
              <Input
                w="50px"
                bg="transparent"
                type="number"
                h="22px"
                size="unset"
                p={0}
                textAlign="center"
                fontSize="12px"
                border="1px solid"
                borderColor="border.buttonOutline"
                mx="8px"
                _focusVisible={{ boxShadow: "none" }}
                disabled={isMultiDisabled}
                value={inputVal}
                onBlur={(e) => handleInputBlur(+e.target.value)}
                onKeyDown={handleInputPress}
                onChange={handleInputChange}
              />
              <Icon
                svg={PlusButtonSvg}
                strokeByCurrentColor
                {...iconButtonStyle(isPlusDisabled)}
                onClick={handlePlus}
              />
            </Flex>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              border="none"
              bg="fill.icon"
              rounded="2px"
              w="375px"
              zIndex={1500}
              className={block("tooltip")}
              overflow="hidden"
              __css={{
                overscrollBehavior: "contain",
              }}
            >
              <PopoverArrow bg="fill.icon" />
              <PopoverBody bg="fill.icon" p="8px 12px" color="#ffffff" rounded="2px" w="375px">
                <Box w="100%" lineHeight="22px" fontSize="14px">
                  {lang !== "zh" ? (
                    "To ensure user experience, the function of generating multiple images at the same time is only available to users using the accelerated channel"
                  ) : (
                    <>
                      由于同时生成多张图片对算力消耗较大，为保证用户体验 生成多张的功能仅对使用{" "}
                      <chakra.div
                        className="gold-gradient-text"
                        cursor="pointer"
                        onClick={() => accelerationStore.setPaymentModalOpen(true)}
                      >
                        加速通道
                      </chakra.div>{" "}
                      的用户可用
                    </>
                  )}
                </Box>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </Flex>
    </Flex>
  );
};

export const GenMultiInput = chakra(observer(_GenMultiInput));
